import { useEffect, useRef, useState } from 'react';
import { Target, Eye, Award, Users, Factory, ChevronLeft, ChevronRight } from 'lucide-react';
import { companyInfo, timeline, honors } from '../data/company';

export default function About() {
  const [, setActiveSection] = useState('');
  const timelineRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeYear, setActiveYear] = useState<string | null>(null);
  const sectionRefs = {
    intro: useRef<HTMLDivElement>(null),
    timeline: useRef<HTMLDivElement>(null),
    honors: useRef<HTMLDivElement>(null)
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  const checkScroll = () => {
    if (timelineRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = timelineRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const el = timelineRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      checkScroll();
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, []);

  const scrollTimeline = (direction: 'left' | 'right') => {
    if (timelineRef.current) {
      const scrollAmount = 320;
      timelineRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <main className="pt-20">
      <section className="py-16 md:py-24 bg-gradient-to-br from-deep-lake to-deep-lake-dark">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif-zh font-bold text-white mb-6">
            关于民间艺人
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto">
            十余载匠心传承，专注每一支浮漂的极致品质
          </p>
        </div>
      </section>

      <section id="intro" ref={sectionRefs.intro} className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4">
                Company Profile
              </span>
              <h2 className="text-3xl md:text-4xl font-serif-zh font-bold text-deep-lake mb-6">
                企业简介
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6 whitespace-pre-line">
                {companyInfo.description}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <Factory className="w-6 h-6 text-deep-lake mb-2" />
                  <div className="text-sm text-gray-500">成立时间</div>
                  <div className="font-semibold text-deep-lake">{companyInfo.founded}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <Users className="w-6 h-6 text-deep-lake mb-2" />
                  <div className="text-sm text-gray-500">员工人数</div>
                  <div className="font-semibold text-deep-lake">{companyInfo.employees}</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="/images/gc.png"
                  alt="民间艺人工厂"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-warm-gold/20 rounded-2xl -z-10" />
            </div>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-8 bg-gradient-to-br from-deep-lake to-deep-lake-dark rounded-2xl text-white text-center">
              <div className="flex justify-center">
                <Target className="w-10 h-10 text-warm-gold mb-4" />
              </div>
              <h3 className="text-2xl font-serif-zh font-bold mb-4">使命</h3>
              <p className="text-white/80 leading-relaxed">{companyInfo.mission}</p>
            </div>
            <div className="p-8 bg-gradient-to-br from-warm-gold/20 to-dawn-orange/20 rounded-2xl text-center">
              <div className="flex justify-center">
                <Eye className="w-10 h-10 text-dawn-orange mb-4" />
              </div>
              <h3 className="text-2xl font-serif-zh font-bold text-deep-lake mb-4">愿景</h3>
              <p className="text-gray-600 leading-relaxed">{companyInfo.vision}</p>
            </div>
            <div className="p-8 bg-gradient-to-br from-deep-lake/10 to-deep-lake/20 rounded-2xl border-2 border-deep-lake/20 text-center">
              <div className="flex justify-center">
                <Award className="w-10 h-10 text-deep-lake mb-4" />
              </div>
              <h3 className="text-2xl font-serif-zh font-bold text-deep-lake mb-4">价值观</h3>
              <p className="text-gray-600 leading-relaxed">{companyInfo.values}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="timeline" ref={sectionRefs.timeline} className="py-20 md:py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4">
              Development History
            </span>
            <h2 className="text-3xl md:text-4xl font-serif-zh font-bold text-deep-lake mb-4">
              发展历程
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              从手工作坊到行业领军，每一步都铭刻着匠心的印记
            </p>
          </div>

        {/* 横向上下结构时间线 */}
        <div className="relative">
            {/* 左右滑动按钮 - 放在容器外侧避免重叠 */}
            <button
              onClick={() => scrollTimeline('left')}
              className={`absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-xl border border-gray-100 flex items-center justify-center transition-all hover:shadow-2xl hover:scale-110 ${
                canScrollLeft ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
              }`}
              disabled={!canScrollLeft}
            >
              <ChevronLeft className="w-6 h-6 text-deep-lake" />
            </button>
            <button
              onClick={() => scrollTimeline('right')}
              className={`absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-xl border border-gray-100 flex items-center justify-center transition-all hover:shadow-2xl hover:scale-110 ${
                canScrollRight ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
              }`}
              disabled={!canScrollRight}
            >
              <ChevronRight className="w-6 h-6 text-deep-lake" />
            </button>

            {/* 滑动容器 */}
            <div
              ref={timelineRef}
              className="overflow-x-auto scrollbar-hide px-12"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {/* 时间轴线 - 使用Grid布局确保上下内容都可见 */}
              <div className="relative" style={{ minWidth: 'max-content' }}>
                {/* 上下内容区域 + 中心线容器 */}
                <div className="grid grid-rows-[1fr_auto_1fr] gap-0">
                  {/* 上方内容区域 */}
                  <div className="flex items-end gap-20 pb-6" style={{ minWidth: 'max-content' }}>
                    {timeline.map((item, index) => {
                      const isTop = index % 2 === 0;
                      const isActive = activeYear === item.year;
                      return (
                        <div
                          key={`top-${item.year}`}
                          className="w-72 flex-shrink-0"
                          style={{ visibility: isTop ? 'visible' : 'hidden' }}
                        >
                          <div 
                            className={`bg-white rounded-xl p-5 shadow-md border transition-all duration-300 cursor-pointer ${
                              isActive ? 'border-dawn-orange shadow-lg scale-105' : 'border-gray-100'
                            }`}
                            onClick={() => setActiveYear(item.year)}
                          >
                            <h3 className="text-base font-serif-zh font-bold text-gray-900 mb-2">
                              {item.title}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* 中心时间轴线 + 年份圆点 */}
                  <div className="relative h-16 flex items-center" style={{ minWidth: 'max-content' }}>
                    {/* 中心水平渐变线 - 深湖色到晨曦橙色 */}
                    <div className="absolute left-0 right-0 top-1/2 h-1 bg-gradient-to-r from-deep-lake via-dawn-orange to-deep-lake -translate-y-1/2" />
                    
                    {/* 年份圆点 */}
                    <div className="flex items-center gap-20" style={{ minWidth: 'max-content' }}>
                      {timeline.map((item, index) => {
                        const isActive = activeYear === item.year;
                        const hasTopContent = index % 2 === 0;
                        const hasBottomContent = index % 2 === 1;
                        return (
                          <div
                            key={`dot-${item.year}`}
                            className="w-72 flex-shrink-0 flex items-center justify-center relative cursor-pointer"
                            onClick={() => setActiveYear(item.year)}
                          >
                            {/* 向上连接线 - 只有上方有内容时才显示 */}
                            {hasTopContent && (
                              <div className={`absolute bottom-full left-1/2 w-0.5 h-6 transition-colors duration-300 ${
                                isActive ? 'bg-dawn-orange' : 'bg-gray-300'
                              } -translate-x-1/2`} />
                            )}
                            {/* 年份圆点 */}
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg z-10 transition-all duration-300 ${
                              isActive ? 'bg-dawn-orange scale-110' : 'bg-deep-lake'
                            }`}>
                              {item.year}
                            </div>
                            {/* 向下连接线 - 只有下方有内容时才显示 */}
                            {hasBottomContent && (
                              <div className={`absolute top-full left-1/2 w-0.5 h-6 transition-colors duration-300 ${
                                isActive ? 'bg-dawn-orange' : 'bg-gray-300'
                              } -translate-x-1/2`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* 下方内容区域 */}
                  <div className="flex items-start gap-20 pt-6" style={{ minWidth: 'max-content' }}>
                    {timeline.map((item, index) => {
                      const isBottom = index % 2 === 1;
                      const isActive = activeYear === item.year;
                      return (
                        <div
                          key={`bottom-${item.year}`}
                          className="w-72 flex-shrink-0"
                          style={{ visibility: isBottom ? 'visible' : 'hidden' }}
                        >
                          <div 
                            className={`bg-white rounded-xl p-5 shadow-md border transition-all duration-300 cursor-pointer ${
                              isActive ? 'border-dawn-orange shadow-lg scale-105' : 'border-gray-100'
                            }`}
                            onClick={() => setActiveYear(item.year)}
                          >
                            <h3 className="text-base font-serif-zh font-bold text-gray-900 mb-2">
                              {item.title}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="honors" ref={sectionRefs.honors} className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium text-dawn-orange tracking-wider uppercase mb-4">
              Honors & Awards
            </span>
            <h2 className="text-3xl md:text-4xl font-serif-zh font-bold text-deep-lake mb-4">
              荣誉资质
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              每一份荣誉都是对品质的认可，对匠心的肯定
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {honors.map((honor, index) => (
              <div
                key={index}
                className="group rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={honor.image}
                    alt={honor.title}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


    </main>
  );
}
